import { QuickFilterType } from '@/enums/QuickFilterType'
import {
  isBooleanLike,
  isDateLike,
  isNumberLike,
  normalizeMatchMode,
  type AnyMatchMode,
  type CustomDataTableFilterMeta,
  type CustomDataTableOperatorFilterMetaData
} from '@/models/Filtering/CustomDataTableOperatorFilterMetaData'
import type { DynamicLinqFilter, filterTypes } from '@/models/Filtering/DynamicLinqFilter'
import type { FieldReference } from '@/models/Filtering/FieldReference'
import type { FilterCondition, OrderByCondition } from '@/models/Filtering/FilterCondition'
import { FilterMatchMode } from '@primevue/core/api'
import { injectable } from 'inversify-props'
import type { DataTableFilterMeta, DataTableFilterMetaData, DataTableOperatorFilterMetaData } from 'primevue/datatable'

@injectable()
export class FilteringHelper {
  private filterMeta: CustomDataTableFilterMeta | null = null
  private searchValue: string | null = null
  private collectionFields: FieldReference[] = []
  private customConditions: FilterCondition[] = []
  private orderByCondition: OrderByCondition | null = null

  public updateFilters(filters: CustomDataTableFilterMeta | undefined): DynamicLinqFilter {
    if (filters) {
      this.filterMeta = filters
    }
    return this.convertToDynamicLinqFilter()
  }

  public updateSearch(searchValue: string): DynamicLinqFilter {
    this.searchValue = searchValue
    return this.convertToDynamicLinqFilter()
  }

  public clearSearch(): DynamicLinqFilter {
    this.searchValue = null
    return this.convertToDynamicLinqFilter()
  }

  public clearFilters(): DynamicLinqFilter {
    this.filterMeta = null
    this.collectionFields = []
    return this.convertToDynamicLinqFilter()
  }

  public registerCollectionField(fields: FieldReference[]): void {
    this.collectionFields = fields
  }

  public addCustomCondition(condition: FilterCondition): DynamicLinqFilter {
    if (!this.customConditionExists(condition)) {
      this.customConditions.push({ ...condition })
    }
    return this.convertToDynamicLinqFilter()
  }

  public removeCustomCondition(condition: FilterCondition): DynamicLinqFilter {
    const customCondition = this.customConditions.find(c => c.field === condition.field && c.operator === condition.operator && c.predicate === condition.predicate)
    if (customCondition) {
      this.customConditions.splice(this.customConditions.indexOf(customCondition), 1)
    }
    return this.convertToDynamicLinqFilter()
  }

  public addNotNullCondition(field: string, operator: 'AND' | 'OR' = 'AND'): DynamicLinqFilter {
    return this.addCustomCondition({
      field,
      operator,
      predicate: `${field} != null`
    })
  }

  public removeNotNullCondition(field: string, operator: 'AND' | 'OR' = 'AND'): DynamicLinqFilter {
    return this.removeCustomCondition({
      field,
      operator,
      predicate: `${field} != null`
    })
  }

  public addIsNullCondition(field: string, operator: 'AND' | 'OR' = 'AND'): DynamicLinqFilter {
    return this.addCustomCondition({
      field,
      operator,
      predicate: `${field} == null`
    })
  }

  public removeNullCondition(field: string, operator: 'AND' | 'OR' = 'AND'): DynamicLinqFilter {
    return this.removeCustomCondition({
      field,
      operator,
      predicate: `${field} == null`
    })
  }

  public OrderBy(field: string, direction: 'asc' | 'desc' | undefined): DynamicLinqFilter {
    this.orderByCondition = {
      field,
      order: direction
    }
    return this.convertToDynamicLinqFilter()
  }

  public clearOrderBy(): DynamicLinqFilter {
    this.orderByCondition = null
    return this.convertToDynamicLinqFilter()
  }

  public clearCustomConditions(): DynamicLinqFilter {
    this.customConditions = []
    return this.convertToDynamicLinqFilter()
  }

  public isFiltered(filterMeta: DataTableFilterMeta | null | undefined, filterName: string): boolean {
    if (!filterMeta) {
      return false
    }

    const filter = filterMeta[filterName]

    if (!filter || typeof filter !== 'object') {
      return false
    }

    if ('constraints' in filter && Array.isArray(filter.constraints)) {
      return (
        filter.constraints.length > 0 &&
        filter.constraints[0] !== null &&
        typeof filter.constraints[0] === 'object' &&
        'value' in filter.constraints[0] &&
        filter.constraints[0].value !== null &&
        filter.constraints[0].value !== undefined
      )
    }

    if ('value' in filter) {
      return filter.value !== null && filter.value !== undefined
    }

    return false
  }

  public getCurrentParamIndex(): number {
    let paramCount = 0

    if (this.filterMeta) {
      Object.entries(this.filterMeta).forEach(([_key, filter]) => {
        if (this.isOperatorFilter(filter)) {
          filter.constraints.forEach(constraint => {
            if (constraint.value) {
              paramCount++
            }
          })
        }
      })
    }

    if (this.searchValue) {
      const regularSearchFields = Array.from(
        new Set(
          Object.entries(this.filterMeta || {})
            .filter(([_key, filter]) => this.isOperatorFilter(filter))
            .map(([_key, filter]) => (filter as CustomDataTableOperatorFilterMetaData).constraints[0]?.filterOptions?.filterField || _key)
            .filter(field => field)
        )
      )

      const collectionSearchFields = this.collectionFields.map(field => field.collection)

      paramCount += regularSearchFields.length + collectionSearchFields.length
    }

    this.customConditions.forEach(condition => {
      if (condition.params && condition.params.length > 0) {
        paramCount += condition.params.length
      }
    })

    return paramCount
  }

  private customConditionExists(filterCondition: FilterCondition): boolean {
    return (
      this.customConditions.find(
        c => c.field === filterCondition.field && c.operator === filterCondition.operator && c.predicate === filterCondition.predicate && c.params === filterCondition.params
      ) !== undefined
    )
  }

  private isOperatorFilter(filter: string | DataTableFilterMetaData | DataTableOperatorFilterMetaData): filter is DataTableOperatorFilterMetaData {
    return filter !== null && typeof filter === 'object' && 'operator' in filter && 'constraints' in filter
  }

  private isBasicFilter(filter: string | DataTableFilterMetaData | CustomDataTableOperatorFilterMetaData): filter is DataTableFilterMetaData {
    return filter !== null && typeof filter === 'object' && 'value' in filter && 'matchMode' in filter
  }

  private buildCollectionPredicate(field: FieldReference, matchMode: string, paramIndex: number): string {
    let predicate = `${field.collection} != null && ${field.collection}.Any(`

    switch (matchMode) {
      case FilterMatchMode.CONTAINS:
        predicate += `c => c.${field.property}.ToLower().Contains(@${paramIndex}.ToLower())`
        break
      case FilterMatchMode.STARTS_WITH:
        predicate += `c => c.${field.property}.ToLower().StartsWith(@${paramIndex}.ToLower())`
        break
      case FilterMatchMode.ENDS_WITH:
        predicate += `c => c.${field.property}.ToLower().EndsWith(@${paramIndex}.ToLower())`
        break
      case FilterMatchMode.EQUALS:
        predicate += `c => c.${field.property}.ToLower() = @${paramIndex}.ToLower()`
        break
      case FilterMatchMode.NOT_EQUALS:
        return `${field.property} == null || ${field.property}.All(c => c.${field.property}.ToLower() != @${paramIndex}.ToLower())`
      case FilterMatchMode.LESS_THAN:
        predicate += `c => c.${field.property}.ToLower().CompareTo(@${paramIndex}.ToLower()) < 0`
        break
      case FilterMatchMode.LESS_THAN_OR_EQUAL_TO:
        predicate += `c => c.${field.property}.ToLower().CompareTo(@${paramIndex}.ToLower()) <= 0`
        break
      case FilterMatchMode.GREATER_THAN:
        predicate += `c => c.${field.property}.ToLower().CompareTo(@${paramIndex}.ToLower()) > 0`
        break
      case FilterMatchMode.GREATER_THAN_OR_EQUAL_TO:
        predicate += `c => c.${field.property}.ToLower().CompareTo(@${paramIndex}.ToLower()) >= 0`
        break
      default:
        predicate += `c => c.${field.property}.ToLower().Contains(@${paramIndex}.ToLower())`
    }

    predicate += ')'
    return predicate
  }

  private buildPredicate(field: string, paramIndex: number, matchMode: AnyMatchMode, value: unknown): string {
    const mm = normalizeMatchMode(matchMode)

    if (isBooleanLike(value)) {
      switch (mm) {
        case 'equals':
          return `${field} = @${paramIndex}`
        case 'notEquals':
          return `${field} != @${paramIndex}`
        default:
          return `${field} = @${paramIndex}`
      }
    }

    if (isNumberLike(value)) {
      switch (mm) {
        case 'equals':
          return `${field} = @${paramIndex}`
        case 'notEquals':
          return `${field} != @${paramIndex}`
        case 'lt':
          return `${field} < @${paramIndex}`
        case 'lte':
          return `${field} <= @${paramIndex}`
        case 'gt':
          return `${field} > @${paramIndex}`
        case 'gte':
          return `${field} >= @${paramIndex}`
        default:
          return `${field} = @${paramIndex}`
      }
    }

    if (isDateLike(value)) {
      switch (mm) {
        case 'equals':
          return `${field} = @${paramIndex}`
        case 'notEquals':
          return `${field} != @${paramIndex}`
        case 'dateBefore':
        case 'lt':
          return `${field} < @${paramIndex}`
        case 'dateAfter':
        case 'gt':
          return `${field} > @${paramIndex}`
        case 'lte':
          return `${field} <= @${paramIndex}`
        case 'gte':
          return `${field} >= @${paramIndex}`
        default:
          return `${field} = @${paramIndex}`
      }
    }

    const lowerField = `${field}.ToLower()`
    const lowerParam = `@${paramIndex}.ToLower()`

    switch (mm) {
      case 'contains':
        return `${lowerField}.Contains(${lowerParam})`
      case 'startsWith':
        return `${lowerField}.StartsWith(${lowerParam})`
      case 'endsWith':
        return `${lowerField}.EndsWith(${lowerParam})`
      case 'equals':
        return `${lowerField} = ${lowerParam}`
      case 'notEquals':
        return `${lowerField} != ${lowerParam}`
      case 'lt':
        return `${lowerField}.CompareTo(${lowerParam}) < 0`
      case 'lte':
        return `${lowerField}.CompareTo(${lowerParam}) <= 0`
      case 'gt':
        return `${lowerField}.CompareTo(${lowerParam}) > 0`
      case 'gte':
        return `${lowerField}.CompareTo(${lowerParam}) >= 0`
      default:
        return `${lowerField}.Contains(${lowerParam})`
    }
  }

  private convertToDynamicLinqFilter(): DynamicLinqFilter {
    let filterParts: string[] = []
    const filterParams: Array<filterTypes> = []
    const columnFilteredFields = new Set<string>()
    const searchFields = new Set<string>()
    if (this.filterMeta) {
      Object.entries(this.filterMeta).forEach(([_key, filter]) => {
        if (this.isOperatorFilter(filter)) {
          const operatorFilter = filter
          const field = operatorFilter.constraints[0]?.filterOptions?.filterField || _key || ''
          if (field) {
            let finalPredicate = ''
            searchFields.add(field)

            operatorFilter.constraints.forEach(constraint => {
              if (constraint.value) {
                const constraintField = constraint.filterOptions?.filterField ?? field
                columnFilteredFields.add(constraintField)
                searchFields.add(constraintField)
                const value = constraint.value
                const paramIndex = filterParams.length

                const predicate = this.buildPredicate(constraintField, paramIndex, constraint.matchMode, value)

                finalPredicate = finalPredicate.length > 0 ? `${finalPredicate} ${operatorFilter.operator.toUpperCase()} ${predicate}` : predicate
                filterParams.push(value)
              }
            })

            if (finalPredicate) {
              filterParts.push(`(${finalPredicate})`)
            }
          }
        }

        if (this.isBasicFilter(filter)) {
          const baseField = _key || ''
          if (!baseField || filter.value === null || filter.value === '') {
            return
          }
          searchFields.add(baseField)
          columnFilteredFields.add(baseField)

          const paramIndex = filterParams.length
          const predicate = this.buildPredicate(baseField, paramIndex, filter.matchMode, filter.value)

          filterParams.push(filter.value)
          filterParts.push(`(${predicate})`)
        }
      })
    }

    if (this.searchValue) {
      const regularSearchParts: string[] = []
      const collectionSearchParts: string[] = []
      this.collectionFields
        .filter(column => !columnFilteredFields.has(column.collection))
        .forEach(column => {
          const paramIndex = filterParams.length
          filterParams.push(this.searchValue)
          collectionSearchParts.push(this.buildCollectionPredicate(column, FilterMatchMode.CONTAINS, paramIndex))
        })
      Array.from(searchFields).forEach(column => {
        const paramIndex = filterParams.length
        filterParams.push(this.searchValue)
        regularSearchParts.push(`${column}.ToLower().Contains(@${paramIndex}.ToLower())`)
      })

      if (collectionSearchParts.length > 0 || regularSearchParts.length > 0) {
        const regularSearchPredicate = regularSearchParts.join(' OR ')
        const collectionSearchPredicate = collectionSearchParts.join(' OR ')
        let combinedPredicate = ''

        if (regularSearchPredicate && collectionSearchPredicate) {
          combinedPredicate = `(${regularSearchPredicate} OR ${collectionSearchPredicate})`
        } else if (regularSearchPredicate) {
          combinedPredicate = regularSearchParts.length > 1 ? `(${regularSearchPredicate})` : regularSearchPredicate
        } else if (collectionSearchPredicate) {
          combinedPredicate = collectionSearchParts.length > 1 ? `(${collectionSearchPredicate})` : collectionSearchPredicate
        }
        filterParts.push(combinedPredicate)
      }
    }

    this.customConditions.forEach(condition => {
      if (filterParts.length > 0) {
        if (condition.params && condition.params.length > 0) {
          condition.params.forEach(param => {
            filterParams.push(param)
          })
        }

        if (condition.operator === 'OR') {
          const existingParts = filterParts.join(' AND ')
          filterParts = [`(${existingParts}) OR (${condition.predicate})`]
        } else {
          filterParts.push(`(${condition.predicate})`)
        }
      } else {
        filterParts.push(`(${condition.predicate})`)

        if (condition.params && condition.params.length > 0) {
          condition.params.forEach(param => {
            filterParams.push(param)
          })
        }
      }
    })

    const filterExpression = filterParts.length > 0 ? filterParts.join(' AND ') : null
    let orderByExpression = ''
    if (this.orderByCondition?.field) {
      orderByExpression = `${this.orderByCondition.field} ${this.orderByCondition.order}`
    }

    return {
      FilterExpression: filterExpression,
      FilterParameters: filterParams.length > 0 ? filterParams : null,
      OrderByExpression: orderByExpression
    }
  }
}

export const extractValues = (originalValues: DataTableFilterMetaData, valuesToIgnore: number[] = []): string => {
  let result
  if (originalValues.value === undefined || originalValues.value === null || (Array.isArray(originalValues.value) && originalValues.value.length === 0)) {
    return ''
  }
  if (typeof originalValues.value === 'string') {
    result = originalValues.value.split(',').map(Number)
  } else if (Array.isArray(originalValues.value)) {
    result = originalValues.value.map(Number)
  } else {
    result = [Number(originalValues.value)]
  }
  const filteredResult = result.filter(value => !valuesToIgnore.includes(value))
  return filteredResult.join(',')
}



export function getQuickFilterTypeFromDate(dateFromValue: Date | null | undefined): QuickFilterType | null {
  if (!dateFromValue) {
    return null
  }

  const now = new Date()

  const diffMs = now.getTime() - dateFromValue.getTime()
  const diffMinutes = Math.round(diffMs / 60000)

  if (diffMinutes >= 55 && diffMinutes <= 65) {
    return QuickFilterType.lastHour
  }

  const nowMinus3Days = new Date()
  nowMinus3Days.setDate(now.getDate() - 3)
  const nowMinus1Week = new Date()
  nowMinus1Week.setDate(now.getDate() - 7)
  const nowMinus3Weeks = new Date()
  nowMinus3Weeks.setDate(now.getDate() - 21)

  const isPredefinedDate =
    dateFromValue.toDateString() === nowMinus3Days.toDateString() || dateFromValue.toDateString() === nowMinus1Week.toDateString() || dateFromValue.toDateString() === nowMinus3Weeks.toDateString()

  if (!isPredefinedDate) {
    return null
  } else if (dateFromValue.toDateString() === nowMinus3Days.toDateString()) {
    return QuickFilterType.lastThreeDays
  } else if (dateFromValue.toDateString() === nowMinus1Week.toDateString()) {
    return QuickFilterType.lastWeek
  } else if (dateFromValue.toDateString() === nowMinus3Weeks.toDateString()) {
    return QuickFilterType.lastThreeWeeks
  }

  return null
}